// Aug-2026 (user request) — Blocker Scope Split on the Ops Dashboard:
// Client vs Radiants (vs Other / Not Set) blocker counts so accountability
// for site blockers is obvious at a glance.
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Search } from "lucide-react";
import { api } from "@/lib/api";

const SCOPE_CLS = {
  Client: "bg-sky-50 text-sky-700 border-sky-300",
  Radiants: "bg-orange-50 text-orange-700 border-orange-300",
  Other: "bg-slate-100 text-slate-600 border-slate-300",
  "Not Set": "bg-white text-slate-400 border-slate-200",
};

export default function OpsBlockerScopeCard() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get(`/ops-dashboard/blockers?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); setData((d) => d || { totals: {}, projects: [], recent: [] }); });
  }, [days]);

  const filteredProjects = useMemo(() => {
    const rows = data?.projects || [];
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      (r.project_code || "").toLowerCase().includes(s)
      || (r.project_name || "").toLowerCase().includes(s));
  }, [data, q]);
  const filteredRecent = useMemo(() => {
    const rows = data?.recent || [];
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((b) =>
      (b.project_code || "").toLowerCase().includes(s)
      || (b.project_name || "").toLowerCase().includes(s));
  }, [data, q]);

  if (denied) return null;
  const t = data?.totals || {};

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3 resize-y overflow-auto min-h-[280px] max-h-[85vh]"
      style={{ height: "460px" }} data-testid="ops-blocker-scope-card">
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle size={14} className="text-rose-600" />
        <div className="text-sm font-bold text-slate-900">Blockers — Scope Split</div>
        <div className="text-[10px] text-slate-500">Client vs Radiants accountability · current Submitted DPRs</div>
        <div className="relative ml-auto">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="ops-blocker-search" className="border border-slate-300 rounded pl-6 pr-2 py-1 text-[11px] w-56"
            placeholder="Search project code / name…"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select data-testid="ops-blocker-days" value={days} onChange={(e) => setDays(Number(e.target.value))}
          className="border border-slate-300 rounded px-2 py-1 text-[11px]">
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </div>

      <div className="flex flex-wrap gap-2">
        {["Client", "Radiants", "Other", "Not Set"].map((s) => (
          <div key={s} className={`flex-1 min-w-[110px] border rounded-lg px-3 py-2 ${SCOPE_CLS[s]}`}
            data-testid={`ops-blocker-count-${s.toLowerCase().replace(" ", "-")}`}>
            <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">{s}</div>
            <div className="text-lg font-bold tabular-nums">{t[s] ?? 0}</div>
          </div>
        ))}
        <div className="flex-1 min-w-[110px] border border-slate-300 rounded-lg px-3 py-2 bg-slate-50" data-testid="ops-blocker-count-total">
          <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Total Blockers</div>
          <div className="text-lg font-bold tabular-nums text-slate-900">{t.total ?? 0}</div>
        </div>
      </div>

      {(filteredProjects || []).length > 0 ? (
        <div className="overflow-x-auto overflow-y-auto max-h-[240px] border border-slate-200 rounded" data-testid="ops-blocker-project-scroll">
          <table className="w-full text-xs min-w-[560px]" data-testid="ops-blocker-project-table">
            <thead className="bg-slate-100 sticky top-0 z-10">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
                <th className="px-2 py-1.5">Project</th>
                <th className="px-2 py-1.5 text-right">Client</th>
                <th className="px-2 py-1.5 text-right">Radiants</th>
                <th className="px-2 py-1.5 text-right">Other</th>
                <th className="px-2 py-1.5 text-right">Not Set</th>
                <th className="px-2 py-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((r) => (
                <tr key={r.project_id} className="border-t border-slate-100" data-testid={`ops-blocker-row-${r.project_code}`}>
                  <td className="px-2 py-1.5"><span className="font-mono font-bold text-[11px]">{r.project_code}</span> <span className="text-slate-500">{r.project_name}</span></td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-sky-700 font-bold">{r.client || "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-orange-700 font-bold">{r.radiants || "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.other || "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{r.not_set || "—"}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-[11px] text-slate-400" data-testid="ops-blocker-empty">
          {q ? "No projects match your search." : "No blockers reported on submitted DPRs in this window."}
        </div>
      )}
      {(data?.projects || []).length > 0 && (
        <div className="text-[10px] text-slate-400" data-testid="ops-blocker-row-count">
          {filteredProjects.length} of {(data.projects || []).length} projects{q ? ` · filtered by "${q}"` : ""} · drag the card's bottom edge to resize
        </div>
      )}

      {(filteredRecent || []).length > 0 && (
        <div className="space-y-1" data-testid="ops-blocker-recent">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Recent Blockers</div>
          {filteredRecent.slice(0, 6).map((b, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1">
              <span className="text-slate-500">{b.date}</span>
              <span className="font-mono font-bold text-[10px]">{b.project_code}</span>
              <span className="font-bold">{b.blocker}</span>
              <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase font-bold border rounded ${SCOPE_CLS[b.scope] || SCOPE_CLS["Not Set"]}`}>{b.scope}</span>
              {b.detail && <span className="text-slate-500 truncate max-w-[340px]" title={b.detail}>{b.detail}</span>}
            </div>
          ))}
        </div>
      )}
      <div className="text-[9px] text-slate-400">{data?.note}</div>
    </div>
  );
}
